"use client";

import React, { useState, useEffect } from 'react';
import { apiFetchJson } from '../lib/api';

type VersionData = {
    backend_version?: string;
    frontend_version?: string;
    build_date?: string;
    db_schema_version?: string;
    error?: string;
};

export const COPYRIGHT = "© 2026 Tektena UG";
export const FRONTEND_VERSION = "2.4";

export default function VersionInfo({ detailed = false }: { detailed?: boolean }) {
    const [backendVersion, setBackendVersion] = useState<VersionData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (detailed) {
            const fetchBackendVersion = async () => {
                try {
                    const data = await apiFetchJson<VersionData>('/version');
                    setBackendVersion(data);
                } catch (error) {
                    console.error('Failed to fetch backend version:', error);
                    setBackendVersion({ error: 'Kommunikationsfehler' });
                } finally {
                    setLoading(false);
                }
            };
            fetchBackendVersion();
        } else {
            setLoading(false);
        }
    }, [detailed]);

    if (!detailed) {
        return (
            <div className="p-4 border-t border-border/10 text-[10px] text-text-secondary/60 leading-relaxed font-bold uppercase tracking-wider">
                <div className="mb-1 text-text-secondary/80">
                    {COPYRIGHT}
                </div>
                <div>
                    Version: {FRONTEND_VERSION}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50/50 rounded-2xl border border-border text-sm text-text-secondary leading-relaxed space-y-4">
            <div className="font-black uppercase tracking-widest text-[11px] text-text-secondary/50 mb-2">System-Architektur</div>

            <div className="space-y-4">
                <div className="flex justify-between items-center group">
                    <span className="font-bold text-text-primary">Frontend</span>
                    <div className="flex flex-col items-end">
                        <span className="font-black text-primary">v{FRONTEND_VERSION}</span>
                        <span className="text-[10px] opacity-40">Next.js 16.1 (App Router)</span>
                    </div>
                </div>

                <div className="border-t border-border/50 pt-4">
                    {loading ? (
                        <div className="animate-pulse flex space-x-4">
                            <div className="flex-1 space-y-2 py-1">
                                <div className="h-2 bg-slate-200 rounded"></div>
                                <div className="h-2 bg-slate-200 rounded w-5/6"></div>
                            </div>
                        </div>
                    ) : backendVersion?.error ? (
                        <div className="text-red-500 font-bold flex items-center justify-between">
                            <span>Backend-Status</span>
                            <span>{backendVersion.error}</span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="font-bold text-text-primary">Backend</span>
                                <div className="flex flex-col items-end">
                                    <span className="font-black text-sedenius-navy">v{backendVersion?.backend_version}</span>
                                    <span className="text-[10px] opacity-40">{backendVersion?.build_date}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-bold text-text-primary">DB Schema</span>
                                <span className="px-2 py-0.5 bg-gray-200 rounded font-mono text-xs font-bold">v{backendVersion?.db_schema_version}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
